import React, { useEffect, useMemo, useState } from 'react';
import { api, Document } from '../services/api';
import { statusText, useCabinetLang } from '../i18n';

type PartyInfo = { name: string; iin: string; phone: string };
type SignatureInfo = {
  id: string;
  signerType: string;
  signedAt?: string;
  certificateSubject?: string;
  signatureValid?: boolean | null;
};

function IconSend() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function IconInbox() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M22 12h-6l-2 3H10l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function IconPen() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function IconChevron() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M10 13a5 5 0 0 0 7.54.54l1.92-1.92a5 5 0 0 0-7.07-7.07L11 6" />
      <path d="M14 11a5 5 0 0 0-7.54-.54L4.54 12.38a5 5 0 0 0 7.07 7.07L13 18" />
    </svg>
  );
}

function statusColor(key: string) {
  switch (key) {
    case 'COMPLETED':
    case 'SENT':
      return '#2e7d32';
    case 'WAIT_YANDEX':
    case 'IN_PROGRESS':
      return '#ef6c00';
    case 'WAIT_DRIVER':
    case 'WAIT_SEND':
    case 'NEW':
      return '#1565c0';
    case 'OVERDUE':
      return '#e64a19';
    case 'REJECTED':
    case 'CANCELLED':
      return '#c62828';
    default:
      return '#757575';
  }
}

function isEsfDoc(doc: Document) {
  if (doc.source === 'yandex' || !doc.source) return false;
  const type = String(doc.documentType || '').toUpperCase();
  const cat = String(doc.category || doc.metadata?.category || '')
    .trim()
    .toLowerCase();
  return (
    type === 'INVOICE' ||
    type === 'ESF' ||
    cat === 'esf' ||
    cat === 'invoice' ||
    /эсф|счет.?фактур|invoice|esf/i.test(String(doc.documentType || ''))
  );
}

function waitingKeyFromDoc(doc: Document, signatures: SignatureInfo[]): string {
  if (doc.waitingKey) return doc.waitingKey;
  if (isEsfDoc(doc)) {
    const esfStatus = String(doc.metadata?.esfStatus || '').toLowerCase();
    if (esfStatus === 'submitted' || doc.metadata?.esfRegistrationNumber) return 'SENT';
    return 'WAIT_SEND';
  }
  const status = doc.status;
  if (status === 'PROCESSING') return 'PROCESSING';
  if (status === 'CANCELLED') return 'CANCELLED';
  if (status === 'REJECTED') return 'REJECTED';
  if (status === 'COMPLETED') return 'COMPLETED';

  const has = (t: string) =>
    signatures.some(
      (s) => String(s.signerType).toUpperCase() === t && s.signatureValid !== false
    );
  const driver = has('DRIVER');
  const yandex = has('YANDEX');
  if (doc.source === 'park') return driver ? 'COMPLETED' : 'WAIT_DRIVER';
  if (driver && yandex) return 'COMPLETED';
  if (driver && !yandex) return 'WAIT_YANDEX';
  return 'WAIT_DRIVER';
}

function formatDt(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function cnFromSubject(subject?: string) {
  if (!subject) return '';
  const match = String(subject).match(/CN=([^,]+)/i);
  return match ? match[1].trim() : '';
}

function findSignature(signatures: SignatureInfo[], signerType: string) {
  return signatures.find((s) => s.signerType === signerType);
}

function customerSignerType(doc: Document) {
  if (doc.source === 'park') return 'DRIVER';
  return doc.avrSide === 'driver' ? 'YANDEX' : 'DRIVER';
}

function executorSignerType(doc: Document) {
  if (doc.source === 'park') return 'ACCOUNTANT';
  return doc.avrSide === 'driver' ? 'DRIVER' : 'YANDEX';
}

function avrPeriodLabel(doc: Document, months: readonly string[]) {
  const y = Number(doc.metadata?.periodYear);
  const m = Number(doc.metadata?.periodMonth);
  if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
    return `${months[m - 1]} ${y}`;
  }
  const date = doc.metadata?.document_date;
  if (date) {
    const match = String(date).match(/^(\d{4})-(\d{2})/);
    if (match) return `${months[Number(match[2]) - 1] || match[2]} ${match[1]}`;
  }
  return '';
}

function partyFromDoc(doc: Document, side: 'seller' | 'buyer'): PartyInfo {
  const nested = (doc.metadata?.[side] || {}) as Record<string, string>;
  if (side === 'seller') {
    return {
      name: doc.sellerName || nested.name || '—',
      iin: doc.sellerIin || nested.iin || '—',
      phone: doc.sellerPhone || nested.phone || '',
    };
  }
  return {
    name: doc.buyerName || nested.name || '—',
    iin: doc.buyerIin || nested.iin || '—',
    phone: doc.buyerPhone || nested.phone || '',
  };
}

function PartyCard({
  label,
  party,
  signature,
  icon,
}: {
  label: string;
  party: PartyInfo;
  signature?: SignatureInfo;
  icon: React.ReactNode;
}) {
  const { t } = useCabinetLang();
  const signed = Boolean(signature);
  return (
    <div className={`doc-party-card${signed ? ' signed' : ''}`}>
      <div className="doc-party-head">
        <div className="doc-party-role">
          <span className="doc-party-icon">{icon}</span>
          {label}
        </div>
        {signed && (
          <span className="doc-party-signed">
            <span className="doc-party-check">
              <IconCheck />
            </span>
            {t.signedMark}
          </span>
        )}
      </div>
      <div className="doc-party-name">{party.name}</div>
      <div>
        {t.iinBin}: {party.iin}
      </div>
      <div>
        {t.phone}: {party.phone || t.noPhone}
      </div>
    </div>
  );
}

type Props = {
  documentId: string;
  onBack: () => void;
};

export const DocumentDetail: React.FC<Props> = ({ documentId, onBack }) => {
  const { t } = useCabinetLang();
  const signerLabels: Record<string, string> = {
    DRIVER: t.driver,
    YANDEX: t.yandex,
    ACCOUNTANT: t.accountant,
  };
  const [doc, setDoc] = useState<Document | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSigners, setShowSigners] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await api.getDocument(documentId);
        if (!cancelled) setDoc(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t.loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const pdfUrl = useMemo(() => {
    const b64 =
      doc?.signedPdfBase64 ? doc.signedPdfBase64 : doc?.pdfBase64;
    if (!b64) return '';
    try {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    } catch {
      return '';
    }
  }, [doc]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <div className="doc-detail">
        <button type="button" className="doc-detail-back" onClick={onBack}>
          ← {t.backToList}
        </button>
        <div className="no-documents">{t.loading}</div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="doc-detail">
        <button type="button" className="doc-detail-back" onClick={onBack}>
          ← {t.backToList}
        </button>
        <div className="no-documents">{error || t.notFound}</div>
      </div>
    );
  }

  const number = doc.metadata?.document_number || doc.yandexId || doc.id.slice(0, 8);
  const periodLabel = avrPeriodLabel(doc, t.months);
  const seller = partyFromDoc(doc, 'seller');
  const buyer = partyFromDoc(doc, 'buyer');
  const signatures = Array.isArray(doc.signatures) ? doc.signatures : [];
  const waitKey = waitingKeyFromDoc(doc, signatures);
  const waitLabel = statusText(t, waitKey);
  const customerSig = findSignature(signatures, customerSignerType(doc));
  const executorSig = findSignature(signatures, executorSignerType(doc));
  const cat = String(doc.category || doc.metadata?.category || '').toLowerCase();
  const type = String(doc.documentType || '');
  const isEsf =
    doc.source !== 'yandex' &&
    (cat === 'esf' ||
      cat === 'invoice' ||
      type.toUpperCase() === 'INVOICE' ||
      /эсф|счет.?фактур|invoice|esf/i.test(type));
  const isOther =
    cat === 'offer' ||
    cat === 'contract' ||
    cat === 'other' ||
    type.toUpperCase() === 'CONTRACT' ||
    /договор|оферт|offer|contract/i.test(type);
  const title = isEsf
    ? `Счёт-фактура ${number ? `№${number}` : ''}`
    : isOther
      ? `${doc.documentType || 'Документ'} ${number ? `№${number}` : ''}`
      : `${t.avrTitle} ${number ? `№${number}` : ''}`;
  const subtitle =
    periodLabel ||
    (isEsf ? 'ЭСФ' : isOther ? t.type : doc.avrSide === 'driver' ? t.incomingAvr : t.outgoingAvr);

  return (
    <div className="doc-detail">
      <button type="button" className="doc-detail-back" onClick={onBack}>
        ← {t.backToList}
      </button>

      <div className="doc-detail-head">
        <div>
          <h2>{title}</h2>
          <div className="doc-detail-sub">{subtitle}</div>
        </div>
        <div className="doc-detail-badges">
          <span className="status-badge" style={{ background: statusColor(waitKey) }}>
            {waitLabel}
            {waitKey === 'COMPLETED' ? ` ${formatDt(doc.updatedAt)}` : ''}
          </span>
        </div>
      </div>

      <div className="doc-parties">
        <PartyCard label={t.customer} party={buyer} signature={customerSig} icon={<IconSend />} />
        <PartyCard label={t.executor} party={seller} signature={executorSig} icon={<IconInbox />} />
      </div>

      <div className="doc-signers-bar">
        <div className="doc-signers-left">
          <span className="doc-party-icon">
            <IconPen />
          </span>
          {t.signers}
        </div>
        <button
          type="button"
          className={`doc-signers-arrow${showSigners ? ' open' : ''}`}
          onClick={() => setShowSigners((v) => !v)}
          aria-label={t.showSigners}
        >
          <IconChevron />
        </button>
      </div>
      {showSigners && (
        <div className="doc-signers-list">
          {signatures.length === 0 && <div>{t.noSignatures}</div>}
          {signatures.map((sig) => (
            <div key={sig.id} className="doc-signer-row">
              <strong>{cnFromSubject(sig.certificateSubject) || signerLabels[sig.signerType] || sig.signerType}</strong>
              {sig.signedAt ? ` · ${formatDt(sig.signedAt)}` : ''}
            </div>
          ))}
        </div>
      )}

      <div className="doc-meta-line">
        <span>
          {t.regNo} {doc.id}
        </span>
        <span>
          {t.uploadedAt} {formatDt(doc.createdAt)}
        </span>
      </div>

      <div className="doc-detail-actions">
        <button
          type="button"
          className="btn-apply"
          onClick={() => api.downloadDocument(documentId).catch((e) => setError(e.message))}
          disabled={!doc.pdfBase64 && !doc.signedPdfBase64}
        >
          {t.downloadPdf}
        </button>
        <button
          type="button"
          className="btn-outline"
          onClick={() => api.downloadZip(documentId).catch((e) => setError(e.message))}
          disabled={!doc.pdfBase64 && !doc.signedPdfBase64}
        >
          {t.downloadZip}
        </button>
        <button
          type="button"
          className={`doc-icon-btn${copied ? ' copied' : ''}`}
          disabled={!doc.pdfBase64}
          title={copied ? t.linkCopied : t.copySignLink}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(api.publicSignUrl(documentId));
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1600);
            } catch (err) {
              setError(err instanceof Error ? err.message : t.copyLinkFailed);
            }
          }}
        >
          {copied ? <IconCheck /> : <IconLink />}
        </button>
      </div>

      <div className="doc-preview">
        {pdfUrl ? (
          <iframe title="PDF" src={pdfUrl} />
        ) : (
          <div className="no-documents">
            {t.pdfPending}
          </div>
        )}
      </div>
    </div>
  );
};
